import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Telescope, Search, Loader2, Sparkles, Target, Lightbulb, ExternalLink, ArrowRight, TrendingUp, Globe, Check, AlertCircle, Zap, BookOpen, Trophy } from "lucide-react";
import DashboardLayout from "@/components/DashboardLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import api, { formatApiError } from "@/lib/api";
import { useProjects } from "@/context/ProjectContext";
import { toast } from "sonner";

export default function ExplorerPage() {
  const { active } = useProjects();
  const [url, setUrl] = useState("");
  const [analyzing, setAnalyzing] = useState(false);
  const [result, setResult] = useState(null);
  const [history, setHistory] = useState([]);
  const [selectedGaps, setSelectedGaps] = useState(new Set());
  const [generating, setGenerating] = useState(false);
  const [generatedPlan, setGeneratedPlan] = useState(null);

  const loadHistory = () => {
    api.get(`/explorer/analyses${active?.id ? `?project_id=${active.id}` : ""}`)
      .then(({ data }) => setHistory(data))
      .catch(() => {});
  };
  useEffect(() => { loadHistory(); /* eslint-disable-next-line */ }, [active?.id]);

  const analyze = async (e) => {
    e?.preventDefault();
    if (!url.trim()) { toast.error("Paste a competitor URL"); return; }
    setAnalyzing(true);
    setResult(null);
    setGeneratedPlan(null);
    setSelectedGaps(new Set());
    try {
      const { data } = await api.post("/explorer/analyze", { url: url.trim(), project_id: active?.id });
      setResult(data);
      // Pre-select top 5 highest-opportunity gaps
      const top = [...(data.content_gaps || [])]
        .sort((a, b) => (b.opportunity_score || 0) - (a.opportunity_score || 0))
        .slice(0, 5)
        .map(g => g.keyword);
      setSelectedGaps(new Set(top));
      toast.success("Analysis complete");
      loadHistory();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setAnalyzing(false);
    }
  };

  const loadAnalysis = async (id) => {
    try {
      const { data } = await api.get(`/explorer/analyses/${id}`);
      setResult(data);
      setUrl(data.url);
      setGeneratedPlan(null);
      const top = [...(data.content_gaps || [])]
        .sort((a, b) => (b.opportunity_score || 0) - (a.opportunity_score || 0))
        .slice(0, 5)
        .map(g => g.keyword);
      setSelectedGaps(new Set(top));
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const toggleGap = (kw) => {
    const next = new Set(selectedGaps);
    if (next.has(kw)) next.delete(kw); else next.add(kw);
    setSelectedGaps(next);
  };

  const generateFromGaps = async () => {
    if (!result?.id || !active?.id) return;
    if (selectedGaps.size === 0) { toast.error("Select at least one keyword"); return; }
    setGenerating(true);
    try {
      const { data } = await api.post("/explorer/generate-from-gaps", {
        analysis_id: result.id,
        project_id: active.id,
        keywords: Array.from(selectedGaps),
      });
      setGeneratedPlan(data);
      toast.success(`Created plan with ${data.post_count} posts`);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setGenerating(false);
    }
  };

  return (
    <DashboardLayout
      eyebrow="Site Explorer"
      title="Spy on any competitor."
      subtitle="Paste a URL → AI analyzes their SEO, finds gaps you can win, and turns them into a one-click content plan."
    >
      <div className="space-y-6" data-testid="explorer-page">
        {/* URL input */}
        <form onSubmit={analyze} className="flex gap-2 flex-wrap" data-testid="explorer-form">
          <div className="flex-1 min-w-[280px]">
            <div className="flex items-center bg-zinc-950 border border-zinc-800 rounded-sm focus-within:border-[#0055FF] transition-colors">
              <Globe className="w-4 h-4 text-zinc-500 ml-3.5" />
              <Input
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                placeholder="competitor.com  or  https://competitor.com/page"
                className="h-12 bg-transparent border-0 rounded-sm focus-visible:ring-0 text-base"
                data-testid="explorer-url-input"
              />
            </div>
          </div>
          <Button
            type="submit"
            disabled={analyzing}
            className="bg-[#0055FF] hover:bg-[#3377FF] rounded-sm h-12 px-6"
            data-testid="explorer-analyze-btn"
          >
            {analyzing ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Analyzing…</> : <><Telescope className="w-4 h-4 mr-2" /> Explore</>}
          </Button>
        </form>

        {/* History strip */}
        {history.length > 0 && !result && (
          <div className="space-y-2" data-testid="explorer-history">
            <div className="text-[10px] tracking-[0.2em] uppercase text-zinc-500">Recent analyses</div>
            <div className="flex gap-2 flex-wrap">
              {history.slice(0, 10).map((h) => (
                <button
                  key={h.id}
                  onClick={() => loadAnalysis(h.id)}
                  className="text-xs bg-zinc-950 hover:bg-zinc-900 border border-zinc-800 hover:border-zinc-700 rounded-sm px-3 py-1.5 text-zinc-300 transition-colors font-mono"
                  data-testid={`explorer-history-${h.id}`}
                >
                  {h.domain}
                </button>
              ))}
            </div>
          </div>
        )}

        {analyzing && (
          <div className="border border-zinc-800 rounded-sm p-12 bg-zinc-950/40 text-center" data-testid="explorer-loading">
            <div className="inline-flex items-center gap-2 text-zinc-300">
              <Loader2 className="w-5 h-5 animate-spin text-[#3377FF]" />
              <span className="font-display text-lg">Scraping & analyzing…</span>
            </div>
            <p className="text-zinc-500 text-sm mt-2">Fetching their site · extracting signals · AI synthesizing insights</p>
          </div>
        )}

        {/* Results */}
        {result && <Results result={result} selectedGaps={selectedGaps} toggleGap={toggleGap} generateFromGaps={generateFromGaps} generating={generating} generatedPlan={generatedPlan} />}
      </div>
    </DashboardLayout>
  );
}

function Results({ result, selectedGaps, toggleGap, generateFromGaps, generating, generatedPlan }) {
  return (
    <div className="space-y-5 fade-in" data-testid="explorer-results">
      {/* Snapshot */}
      <div className="border border-zinc-800 rounded-sm bg-zinc-950/40 p-6" data-testid="explorer-snapshot">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-sm bg-[#0055FF]/15 border border-[#0055FF]/40 grid place-items-center flex-shrink-0">
              <Telescope className="w-5 h-5 text-[#3377FF]" />
            </div>
            <div>
              <div className="text-[10px] tracking-[0.2em] uppercase text-zinc-500">Analyzed</div>
              <h2 className="font-display text-2xl font-semibold tracking-tight font-mono text-zinc-100">{result.domain}</h2>
            </div>
          </div>
          <a href={result.url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-xs text-[#3377FF] hover:text-white transition-colors">
            <ExternalLink className="w-3 h-3" /> Visit site
          </a>
        </div>
        <p className="text-zinc-300 leading-relaxed mt-4">{result.snapshot}</p>
        {result.positioning && (
          <div className="mt-3 inline-flex items-center gap-2 px-3 py-1.5 bg-zinc-950 border border-zinc-800 rounded-sm">
            <Sparkles className="w-3 h-3 text-[#F59E0B]" />
            <span className="text-xs text-zinc-300">{result.positioning}</span>
          </div>
        )}
        {!result.ai_powered && (
          <div className="mt-3 inline-flex items-center gap-2 text-xs text-amber-400">
            <AlertCircle className="w-3 h-3" /> AI synthesis unavailable — showing extracted signals only.
          </div>
        )}
      </div>

      {/* Top keywords */}
      {(result.top_keywords || []).length > 0 && (
        <div className="border border-zinc-800 rounded-sm bg-zinc-950/40 p-6" data-testid="explorer-keywords">
          <div className="flex items-center gap-2 mb-4">
            <Target className="w-4 h-4 text-[#3377FF]" />
            <h3 className="font-display font-medium">Their top keywords</h3>
          </div>
          <div className="flex flex-wrap gap-2">
            {result.top_keywords.map((kw, i) => (
              <Badge key={i} variant="outline" className="rounded-sm border-zinc-700 bg-zinc-950 text-zinc-300 text-sm py-1.5 px-3">
                {kw}
              </Badge>
            ))}
          </div>
        </div>
      )}

      {/* Content gaps + Generate CTA */}
      {(result.content_gaps || []).length > 0 && (
        <div className="border border-[#0055FF]/40 rounded-sm bg-[#0055FF]/5 p-6" data-testid="explorer-gaps">
          <div className="flex items-start justify-between gap-4 flex-wrap mb-5">
            <div className="flex items-center gap-2">
              <Trophy className="w-5 h-5 text-[#3377FF]" />
              <div>
                <h3 className="font-display text-lg font-semibold tracking-tight">Content gaps you can win</h3>
                <p className="text-xs text-zinc-400 mt-0.5">Keywords your competitor covers that you don't yet. Pick the ones to attack.</p>
              </div>
            </div>
            <Button
              onClick={generateFromGaps}
              disabled={generating || selectedGaps.size === 0}
              className="bg-[#0055FF] hover:bg-[#3377FF] rounded-sm h-11 px-5 shadow-lg shadow-[#0055FF]/20"
              data-testid="explorer-generate-btn"
            >
              {generating ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Generating…</> : <><Zap className="w-4 h-4 mr-2" /> Generate content plan ({selectedGaps.size})</>}
            </Button>
          </div>

          <div className="space-y-2" data-testid="explorer-gaps-list">
            {result.content_gaps.map((g, i) => {
              const selected = selectedGaps.has(g.keyword);
              return (
                <button
                  key={i}
                  type="button"
                  onClick={() => toggleGap(g.keyword)}
                  className={`w-full text-left flex items-center gap-3 p-3 border rounded-sm transition-colors ${selected ? "border-[#0055FF] bg-[#0055FF]/15" : "border-zinc-800 bg-zinc-950/60 hover:border-zinc-700"}`}
                  data-testid={`explorer-gap-${i}`}
                >
                  <div className={`w-5 h-5 rounded-sm border-2 flex-shrink-0 grid place-items-center transition-colors ${selected ? "border-[#0055FF] bg-[#0055FF]" : "border-zinc-700"}`}>
                    {selected && <Check className="w-3 h-3 text-white" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-sm">{g.keyword}</div>
                    <div className="text-xs text-zinc-500 mt-0.5">{g.why}</div>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <Badge variant="outline" className="rounded-sm border-zinc-700 text-[10px] uppercase tracking-widest text-zinc-500">{g.intent}</Badge>
                    <div className="flex items-center gap-1 text-xs">
                      <span className="text-[10px] tracking-widest uppercase text-zinc-500">Opp</span>
                      <span className="font-mono font-semibold text-[#3377FF]">{g.opportunity_score}</span>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>

          {generatedPlan && (
            <div className="mt-4 p-4 rounded-sm bg-[#10B981]/10 border border-[#10B981]/30 flex items-center justify-between gap-3 flex-wrap" data-testid="explorer-generated-plan">
              <div className="flex items-center gap-2 text-sm text-[#10B981]">
                <Check className="w-4 h-4" />
                <strong>Created a {generatedPlan.post_count}-post content plan.</strong>
                <span className="text-zinc-400">Run it through Autopilot to start writing.</span>
              </div>
              <Button asChild size="sm" className="bg-[#0055FF] hover:bg-[#3377FF] rounded-sm">
                <Link to="/autopilot" data-testid="explorer-go-autopilot">Open Autopilot <ArrowRight className="w-3.5 h-3.5 ml-1.5" /></Link>
              </Button>
            </div>
          )}
        </div>
      )}

      {/* Quick wins */}
      {(result.quick_wins || []).length > 0 && (
        <div className="border border-zinc-800 rounded-sm bg-zinc-950/40 p-6" data-testid="explorer-quickwins">
          <div className="flex items-center gap-2 mb-4">
            <Lightbulb className="w-4 h-4 text-[#F59E0B]" />
            <h3 className="font-display font-medium">Quick wins</h3>
          </div>
          <ul className="space-y-2">
            {result.quick_wins.map((q, i) => (
              <li key={i} className="flex items-start gap-3 text-sm text-zinc-300">
                <TrendingUp className="w-3.5 h-3.5 text-[#10B981] mt-0.5 flex-shrink-0" />
                <span>{q}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Top pages */}
      {(result.top_pages || []).length > 0 && (
        <div className="border border-zinc-800 rounded-sm bg-zinc-950/40 p-6" data-testid="explorer-top-pages">
          <div className="flex items-center gap-2 mb-4">
            <BookOpen className="w-4 h-4 text-[#3377FF]" />
            <h3 className="font-display font-medium">Important pages on their site</h3>
          </div>
          <div className="space-y-1.5">
            {result.top_pages.slice(0, 12).map((p, i) => (
              <div key={i} className="flex items-center gap-3 text-sm py-1.5 border-b border-zinc-900 last:border-0">
                <span className="font-mono text-xs text-[#3377FF] truncate max-w-[40%]" title={p.path}>{p.path}</span>
                <span className="text-zinc-400 truncate flex-1">{p.text || "—"}</span>
                <a href={`${result.url}${p.path.startsWith("/") ? p.path : "/" + p.path}`} target="_blank" rel="noreferrer" className="text-zinc-500 hover:text-zinc-200 transition-colors flex-shrink-0">
                  <ExternalLink className="w-3 h-3" />
                </a>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
